<script lang="ts">
	// WhatsApp and Telegram orders, as "Home Visibility" draws them: a chat
	// plays as a loop of beats, each a real state of the flow (sent, read,
	// parsing, parsed, confirmed), and the till's panel follows it. Not built
	// yet (plan section 16, milestone 6); shown without «قريبًا» at the owner's
	// decision (2026-10-03).
	//
	// With reduced motion the loop does not run: the chat shows its last beat.

	import { prefersReducedMotion } from 'svelte/motion';
	import Button from '../components/Button.svelte';
	import Card from '../components/Card.svelte';
	import Eyebrow from '../components/Eyebrow.svelte';
	import Icon from '../components/Icon.svelte';
	import Loading from '../components/Loading.svelte';
	import Row from '../components/Row.svelte';
	import Segmented from '../components/Segmented.svelte';
	import TablePanel from '../components/TablePanel.svelte';
	import { orders, type Channel } from './copy';

	// How long each beat holds, in milliseconds, as the design times them.
	const beats = [1700, 900, 1900, 2100, 1100, 1500, 4300];
	const last = beats.length - 1;

	let chat = $state(0);
	let channel = $state<Channel>('wa');

	const still = $derived(prefersReducedMotion.current);
	const beat = $derived(still ? last : chat);
	const step = $derived(beat <= 2 ? 0 : beat <= 4 ? 1 : 2);
	const draft = $derived(beat === 0 ? orders.draft1 : beat === 4 ? orders.message2 : '');
	const wa = $derived(channel === 'wa');
	const done = $derived(beat >= 6);
	const options = orders.channels.map((option) => ({
		...option,
		testId: `site-orders-${option.value}`,
	}));

	$effect(() => {
		if (still) return;
		const timer = setTimeout(() => (chat = (chat + 1) % beats.length), beats[chat]);
		return () => clearTimeout(timer);
	});

	function pickChannel(value: string) {
		channel = value as Channel;
		chat = 0;
	}
</script>

<section id="orders" class="section">
	<div class="split">
		<div>
			<div class="kicker">
				<Eyebrow tone="speed" text={orders.eyebrow} />
			</div>
			<h2 class="h2">{orders.title}</h2>
			<p class="copy intro">{orders.body}</p>
			<div class="channel">
				<Segmented
					label={orders.channelLabel}
					{options}
					value={channel}
					onChange={pickChannel}
					testId="site-orders-channel"
				/>
			</div>
			<ol class="steps">
				{#each orders.steps as text, index (text)}
					<li class:now={index === step}>
						<span class="figure n">0{index + 1}</span>
						<span>{text}</span>
					</li>
				{/each}
			</ol>
		</div>

		<div class="stack">
			<Card testId="site-orders-chat">
				<div class="chat-head">
					<Icon name="back" label={orders.back} />
					<span class="avatar" aria-hidden="true">{orders.initial}</span>
					<div class="who">
						<p class="who-name">{orders.restaurant}</p>
						<p class="who-status" class:live={beat === 2}>
							{beat === 2 ? orders.typing : wa ? orders.replyingWa : orders.replyingTg}
						</p>
					</div>
					<Icon name="phone" label={orders.call} />
					<Icon name="more" label={orders.more} />
				</div>

				<div class="messages">
					<p class="channel-note" dir="auto">{wa ? orders.channelNoteWa : orders.channelNoteTg}</p>
					{#if beat >= 1}
						<div class="out">
							<div class="bubble mine">
								<p>{orders.message1}</p>
								<div class="meta">
									<span class="figure">7:42</span>
									<Icon name={beat >= 2 ? 'checks' : 'check'} size={16} />
								</div>
							</div>
						</div>
					{/if}
					{#if beat === 2}
						<div class="bubble theirs reading">
							<Loading size="xs" label={orders.reading} caption={orders.readingCaption} />
						</div>
					{/if}
					{#if beat >= 3}
						<div class="bubble theirs">
							<p>{orders.replyStart}</p>
							<div class="lines">
								{#each orders.lines as line (line.name)}
									<div class="line">
										<span
											><span class="figure">{line.qty}×</span>
											{line.name}{#if line.note}<span class="muted"> · {line.note}</span>{/if}</span
										>
										<span class="figure strong">{line.price}</span>
									</div>
								{/each}
								<div class="line total">
									<span>{orders.totalLabel}</span>
									<span class="figure">{orders.total}</span>
								</div>
							</div>
							<p>{orders.replyEnd}</p>
							<p class="figure time">7:42</p>
						</div>
					{/if}
					{#if beat >= 5}
						<div class="out">
							<div class="bubble mine">
								<p>{orders.message2}</p>
								<div class="meta">
									<span class="figure">7:43</span>
									<Icon name="checks" size={16} />
								</div>
							</div>
						</div>
					{/if}
					{#if done}
						<div class="bubble theirs">
							<p>
								<span class="tick">✓</span>
								{orders.confirmedStart}<span class="figure strong">{orders.confirmedMinutes}</span
								>{orders.confirmedEnd}
							</p>
							<p class="figure time">7:44</p>
						</div>
					{/if}
				</div>

				<div class="composer">
					<Icon name="attach" label={orders.attach} />
					<div class="field" class:typed={!!draft}>{draft || orders.placeholder}</div>
					<span class="send">
						<Icon name={draft ? 'send' : 'mic'} label={draft ? orders.send : orders.voice} />
					</span>
				</div>
			</Card>

			<div class="till">
				<TablePanel
					title={orders.cashierTitle}
					note={done ? orders.noteDone : beat >= 3 ? orders.noteUnconfirmed : orders.noteWaiting}
					columns={orders.cashierColumns}
					rowMin="360px"
					testId="site-orders-till"
				>
					<!-- The rows are always drawn, under the empty state until the
					     order arrives, so the panel keeps one height through the loop. -->
					<div class="rows">
						{#if beat < 3}
							<div class="empty">{orders.cashierEmpty}</div>
						{/if}
						{#each orders.lines as line (line.name)}
							<Row state={done ? 'marked' : 'unconfirmed'}>
								<span class="figure cell">{line.qty}</span>
								<span class="item"
									>{line.name}{#if line.note}<span class="item-note">{line.note}</span>{/if}</span
								>
								<span class="figure cell strong">{line.price}</span>
							</Row>
						{/each}
					</div>
				</TablePanel>
				<div class="print">
					<Button
						testId="site-orders-print"
						tone="primary"
						size="sm"
						label={orders.confirmPrint}
						doneLabel={orders.printed}
						state={done ? 'done' : 'idle'}
					/>
					<span class="caption">
						{done ? orders.hintDone : beat >= 3 ? orders.hintUnconfirmed : orders.hintWaiting}
					</span>
				</div>
			</div>
		</div>
	</div>
</section>

<style>
	.split {
		display: grid;
		grid-template-columns: repeat(auto-fit, minmax(min(100%, 360px), 1fr));
		gap: var(--space-12);
		align-items: start;
	}

	.intro {
		margin-block-end: var(--space-9);
	}

	.channel {
		max-inline-size: 260px;
		margin-block-end: var(--space-9);
	}

	.steps {
		margin: 0;
		padding: 0;
		list-style: none;
		display: flex;
		flex-direction: column;
	}

	.steps li {
		display: grid;
		grid-template-columns: var(--space-9) minmax(0, 1fr);
		gap: var(--space-5);
		padding-block: var(--space-5);
		border-block-start: var(--border-width) solid var(--border-default);
		font-size: var(--text-xs);
		line-height: var(--leading-snug);
		color: var(--text-muted);
	}

	.steps li.now {
		color: var(--text-strong);
		font-weight: var(--weight-semibold);
	}

	.n {
		font-size: var(--text-2xs);
		font-weight: var(--weight-semibold);
	}

	.now .n {
		color: var(--accent-speed);
	}

	.stack {
		display: flex;
		flex-direction: column;
		gap: var(--grid-gap);
	}

	.chat-head {
		display: flex;
		align-items: center;
		gap: var(--space-5);
		padding: var(--space-5) var(--space-7);
		background: var(--surface-card-header);
		border-block-end: var(--border-width) solid var(--border-default);
		color: var(--text-muted);
	}

	.avatar {
		inline-size: 38px;
		block-size: 38px;
		border-radius: var(--radius-pill);
		background: var(--speed);
		color: var(--on-speed);
		display: grid;
		place-items: center;
		font-size: var(--text-base);
		font-weight: var(--weight-bold);
		flex: none;
	}

	.who {
		flex: 1;
		min-inline-size: 0;
	}

	.who-name {
		margin: 0;
		font-size: var(--text-xs);
		font-weight: var(--weight-semibold);
		color: var(--text-strong);
	}

	.who-status {
		margin: 0;
		font-size: var(--text-micro);
		color: var(--text-muted);
	}

	.who-status.live {
		color: var(--accent-visibility);
	}

	.messages {
		block-size: 430px;
		display: flex;
		flex-direction: column;
		justify-content: flex-end;
		gap: var(--space-4);
		padding: var(--space-7);
		background: var(--surface-page);
		overflow: hidden;
	}

	.channel-note {
		margin: 0 auto var(--space-3);
		font-family: var(--font-mono);
		font-size: var(--text-micro);
		color: var(--text-muted);
		background: var(--surface-card);
		border: var(--border-width) solid var(--border-default);
		border-radius: var(--radius-pill);
		padding: 2px var(--space-5);
	}

	.out {
		align-self: flex-end;
		max-inline-size: 80%;
		animation: bosla-rise var(--dur-enter) var(--ease-out) both;
	}

	.bubble {
		border-radius: var(--radius-card);
		font-size: var(--text-xs);
		line-height: var(--leading-snug);
	}

	.bubble p {
		margin: 0;
	}

	.mine {
		background: var(--surface-invert);
		color: var(--on-ink);
		border-end-end-radius: var(--radius-chip);
		padding: var(--space-4) var(--space-6) var(--space-3);
	}

	.meta {
		display: flex;
		justify-content: flex-end;
		align-items: center;
		gap: var(--space-1);
		margin-block-start: 2px;
		color: var(--on-ink-muted);
		font-size: var(--text-micro);
	}

	.theirs {
		align-self: flex-start;
		max-inline-size: 86%;
		background: var(--surface-card);
		border: var(--border-width) solid var(--border-default);
		border-end-start-radius: var(--radius-chip);
		padding: var(--space-5) var(--space-6) var(--space-3);
		animation: bosla-rise var(--dur-enter) var(--ease-out) both;
	}

	.reading {
		display: flex;
		align-items: center;
		padding: var(--space-4) var(--space-6);
		color: var(--text-strong);
	}

	.lines {
		display: flex;
		flex-direction: column;
		gap: var(--space-1);
		margin-block: var(--space-3);
		font-size: var(--text-2xs);
	}

	.line {
		display: flex;
		justify-content: space-between;
		gap: var(--space-5);
	}

	.total {
		padding-block-start: var(--space-2);
		margin-block-start: var(--space-1);
		border-block-start: var(--border-width) dashed var(--border-default);
		font-weight: var(--weight-bold);
	}

	.muted {
		color: var(--text-muted);
	}

	.strong {
		font-weight: var(--weight-semibold);
	}

	.time {
		margin-block-start: 2px;
		text-align: start;
		font-size: var(--text-micro);
		color: var(--text-muted);
	}

	.tick {
		color: var(--accent-visibility);
		font-weight: var(--weight-bold);
	}

	.composer {
		display: flex;
		align-items: center;
		gap: var(--space-4);
		padding: var(--space-4) var(--space-6);
		background: var(--surface-card-header);
		border-block-start: var(--border-width) solid var(--border-default);
		color: var(--text-muted);
	}

	.field {
		flex: 1;
		min-inline-size: 0;
		min-block-size: 40px;
		display: flex;
		align-items: center;
		padding: var(--space-3) var(--space-7);
		background: var(--surface-card);
		border: var(--border-width) solid var(--border-default);
		border-radius: var(--radius-pill);
		font-size: var(--text-2xs);
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	.field.typed {
		color: var(--text-strong);
	}

	.send {
		inline-size: 40px;
		block-size: 40px;
		border-radius: var(--radius-pill);
		background: var(--speed);
		color: var(--on-speed);
		display: grid;
		place-items: center;
		flex: none;
	}

	.till {
		display: flex;
		flex-direction: column;
		gap: var(--space-5);
	}

	.rows {
		position: relative;
	}

	.empty {
		position: absolute;
		inset: 0;
		z-index: 1;
		display: grid;
		place-items: center;
		background: var(--surface-card);
		font-size: var(--text-2xs);
		color: var(--text-muted);
	}

	.cell {
		min-inline-size: 0;
		font-size: var(--text-2xs);
		text-align: end;
	}

	.item {
		min-inline-size: 0;
		font-size: var(--text-2xs);
		font-weight: var(--weight-semibold);
	}

	.item-note {
		display: block;
		font-size: var(--text-micro);
		font-weight: var(--weight-regular);
		color: var(--text-muted);
	}

	.print {
		display: flex;
		flex-wrap: wrap;
		gap: var(--space-4);
		align-items: center;
	}
</style>
